import { format, formatISO } from "date-fns";
import i18next, { TFunction } from "i18next";
import { IconButton, ITheme } from "office-ui-fabric-react";
import * as React from "react";
import { BackendError, TaxonomyState } from "../types/types_old";

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const toBase64 = (file: File) => {
  const reader = new FileReader();

  return new Promise<string>((resolve, reject) => {
    reader.onerror = () => {
      reader.abort();
      reject(new DOMException("Problem parsing file"));
    };

    reader.onload = () => {
      resolve(
        reader.result && typeof reader.result === "string"
          ? reader.result.substring(reader.result.indexOf(",") + 1)
          : ""
      );
    };
    reader.readAsDataURL(file);
  });
};

export function removeEmpty(obj: { [key: string]: any }) {
  return Object.entries(obj).reduce((result, [key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
    return result;
  }, {});
}

const formatter = new Intl.NumberFormat("lt-lt", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

export const currencyFormat = (num: number) => {
  if (!num && num !== 0) return;
  return formatter.format(num);
};

export const formatDate = (date: Date) => format(date, "yyyy.MM.dd");

export const formatDateString = (date: string) => {
  return format(new Date(date), "yyyy.MM.dd");
};

export const formatDateStringWithTime = (date: string) => {
  return format(new Date(date), "yyyy.MM.dd kk:mm:ss");
};

export const stringToNumber = (str: string) => {
  let _str = str;
  _str = _str.replace(/\./g, "");
  _str = String(_str).replaceAll(",", ".");
  return parseFloat(_str);
};

const getBackground = (s: any, theme: ITheme) => {
  const { isDisabled, isSelected, isFocused } = s;

  let backgroundColor = theme.palette.white;

  if (isSelected) {
    backgroundColor = theme.palette.neutralLight;
  } else if (isDisabled) {
    backgroundColor = theme.palette.neutralTertiaryAlt;
  } else if (isFocused) {
    backgroundColor = theme.palette.neutralLighter;
  }

  return backgroundColor;
};

const getHoverBackground = (s: any, theme: ITheme) => {
  const { isDisabled, isSelected } = s;

  let backgroundColor = theme.palette.neutralLighter;

  if (isSelected) {
    backgroundColor = theme.palette.neutralLight;
  } else if (isDisabled) {
    backgroundColor = theme.palette.neutralTertiaryAlt;
  }

  return backgroundColor;
};

export const capitalize = (s: string) => {
  if (s.length === 0) return "";
  return `${s[0]?.toUpperCase()}${s.slice(1)}`;
};

export const sanitizeFileName = (s: string) => {
  return s.replace(/[^a-z0-9]/gi, "_").toLowerCase();
};

export const isPhysicalPersonId = (personId: string): boolean => {
  if (!personId) return true;
  return !!personId && personId.length === 11;
};
export const determineCity = (
  address: string,
  taxonomy: TaxonomyState
): number | null => {
  for (let item of taxonomy.SAGCityOrDistrict.items) {
    const cityName = item.code
      .replaceAll("SAGCityOrDistrict.", "")
      .replaceAll(" sav.", "")
      .toUpperCase();
    if (address && address.toUpperCase().indexOf(cityName) >= 0) {
      return item.id;
    }
  }
  for (let item of Object.keys(specialCities)) {
    const cityName = item.toUpperCase();
    if (address && address.toUpperCase().indexOf(cityName) >= 0) {
      return specialCities[cityName];
    }
  }
  return null;
};

const specialCities = {
  "AKMENĖS R": 32,
  "AKMENĖ,": 3209,
  AKMENĖ: 3209,
  "AKMENĖS M": 3209,

  RIETAV: 74,

  "ANYKŠČIŲ R": 34,
  ANYKŠČIAI: 3412, //34 - SAIKAS, 12 - COMPENSA
  "ANYKŠČIŲ M": 3412,

  "BIRŠTONAS,": 12,
  BIRŠTONAS: 12,
  "BIRŠTONO SAV ": 12,

  "BIRŽŲ R": 36,
  "BIRŽŲ M": 3615,
  BIRŽAI: 3615, //36 SAIKAS, 15 COMPENSA

  "DRUSKININKŲ R": 15,
  DRUSKININKAI: 1517,
  "DRUSKININKŲ M": 1517,

  ELEKTRĖNAI: 4220,
  "ELEKTRĖNŲ S": 4220,
  "ELEKTRĖNŲ R": 42,

  "IGNALINOS R": 45,
  IGNALINA: 4522,
  "IGNALINOS M": 4522,

  "JONAVOS R": 46,
  JONAVA: 4624,
  "JONAVOS M": 4624,

  "JONIŠKIO R": 47,
  JONIŠKIS: 4727,
  "JONIŠKIO M": 4727,

  "JURBARKO R": 94,
  JURBARKAS: 9428,
  "JURBARKO M": 9428,

  "KAIŠIADORIŲ R": 49,
  KAIŠIADORYS: 4930,
  "KAIŠIADORIŲ M": 4930,

  "UKMERGĖS R": 81,
  "UKMERGĖ,": 8191,
  "UKMERGĖS M": 8191,
  UKMERGĖ: 8191,

  KALVARIJ: 48,

  "KAZLŲ R": 58,

  "KĖDAINIŲ R": 53,
  KĖDAINIAI: 5335,
  "KĖDAINIŲ M": 5335,

  "KELMĖS R": 54,
  "KELMĖ,": 5437,
  KELMĖ: 5437,
  "KELMĖS M": 5437,

  "KRETINGOS R": 56,
  KRETINGA: 5640,
  "KRETINGOS M": 5640,

  "KUPIŠKIO R": 57,
  KUPIŠKIS: 5743,
  "KUPIŠKIO M": 5743,

  LAZDIJAI: 5944,
  "LAZDIJŲ M": 5944,
  "LAZDIJŲ R": 59,

  "MAŽEIKIŲ R": 61,
  MAŽEIKIAI: 6147,
  "MAŽEIKIŲ M": 6147,

  "MOLĖTŲ R": 62,
  MOLĖTAI: 6249,
  "MOLĖTŲ M": 6249,

  NERINGA: 23,
  "NERINGOS S": 23,
  "NERINGOS M": 23,

  PAGĖGIAI: 63,
  "PAGĖGIŲ M": 63,
  "PAGĖGIŲ S": 63,

  "PAKRUOJO R": 65,
  PAKRUOJIS: 6553,
  "PAKRUOJO M": 6553,

  PALANGA: 25,
  "PALANGOS M": 25,

  "PASVALIO R": 67,
  PASVALYS: 6758,
  "PASVALIO M": 6758,

  "PLUNGĖS R": 68,
  "PLUNGĖ,": 6859,
  PLUNGĖ: 6859,
  "PLUNGĖS M": 6859,

  "PRIENŲ R": 69,
  PRIENAI: 6961,
  "PRIENŲ M": 6961,

  RADVILIŠKIS: 7164,
  "RADVILIŠKIO M": 7164,
  "RADVILIŠKIO R": 71,

  "RASEINIŲ R": 72,
  RASEINIAI: 7265,
  "RASEINIŲ M": 7265,

  "ROKIŠKIO R": 73,
  ROKIŠKIS: 7369,
  "ROKIŠKIO M": 7369,

  "SKUODO R": 75,
  SKUODAS: 7570,
  "SKUODO M": 7570,

  ŠAKIAI: 8472,
  "ŠAKIŲ M": 8472,
  "ŠAKIŲ R": 84,

  "ŠALČININKŲ R": 85,
  ŠALČININKAI: 8574,
  "ŠALČININKŲ M": 8574,

  "ŠILALĖS R": 87,
  ŠILALĖ: 8777,
  "ŠILALĖS M": 8777,

  "ŠILUTĖS R": 88,
  ŠILUTĖ: 8879,
  "ŠILUTĖS M": 8879,

  "ŠIRVINTŲ R": 89,
  ŠIRVINTOS: 8981,
  "ŠIRVINTŲ M": 8981,

  "ŠVENČIONIŲ R": 86,
  ŠVENČIONYS: 8683,
  "ŠVENČIONIŲ M": 8683,
  ŠVENČIONĖL: 86,

  "TAURAGĖS R": 77,
  "TAURAGĖ,": 7785,
  TAURAGĖ: 7785,
  "TAURAGĖS M": 7785,

  "TELŠIŲ R": 78,
  TELŠIAI: 7887,
  "TELŠIŲ M": 7887,

  "TRAKŲ R": 79,
  TRAKAI: 7989,
  "TRAKŲ M": 7989,

  "UTENOS R": 82,
  UTENA: 8293,
  "UTENOS M": 8293,

  "VARĖNOS R": 38,
  VARĖNA: 3895,
  "VARĖNOS M": 3895,

  VILKAVIŠKIS: 3998,
  "VILKAVIŠKIO M": 3998,
  "VILKAVIŠKIO R": 39,

  VISAGIN: 30,

  "ZARASŲ R": 43,
  "ZARASŲ M": 4300,
  ZARASAI: 4300,

  "ALYTAUS R": 33,
  ALYTUS: 11,
  "ALYTAUS M": 11,

  "MARIJAMPOLĖS R": 18,
  "MARIJAMPOLĖ,": 1808,
  MARIJAMPOLĖ: 1808,
  "MARIJAMPOLĖS M": 1808,

  "PANEVĖŽIO R": 66,
  PANEVĖŽYS: 27,
  "PANEVĖŽIO M": 27,

  "ŠIAULIŲ R": 91,
  ŠIAULIAI: 29,
  "ŠIAULIŲ M": 29,

  "KLAIPĖDOS R": 55,
  KLAIPĖDA: 21,
  "KLAIPĖDOS M": 21,

  GARGŽDAI: 55,

  "KAUNO R": 52,
  KAUNAS: 19,
  "KAUNO M": 19,

  "VILNIAUS R": 41,
  "VILNIAUS M.": 13,
  VILNIUS: 13,
};

const cod2aun = {
  "3209": "260",
  "32": "260",
  "11": "2",
  "33": "4",
  "3412": "393",
  "34": "393",
  "12": "42",
  "3615": "206",
  "36": "206",
  "1517": "3",
  "15": "3",
  "4220": "1191",
  "42": "1191",
  "4522": "413",
  "45": "413",
  "4624": "44",
  "46": "44",
  "4727": "267",
  "47": "267",
  "9428": "346",
  "94": "346",
  "4930": "53",
  "49": "53",
  "48": "1192",
  "19": "43",
  "52": "63",
  "58": "1193",
  "5335": "215",
  "53": "215",
  "5437": "278",
  "54": "278",
  "21": "112",
  "55": "115",
  "5640": "127",
  "56": "127",
  "5743": "228",
  "57": "228",
  "5944": "27",
  "59": "27",
  "1808": "162",
  "18": "162",
  "6147": "359",
  "61": "359",
  "6249": "426",
  "62": "426",
  "23": "113",
  "63": "1196",
  "6553": "289",
  "65": "289",
  "25": "114",
  "27": "205",
  "66": "235",
  "6758": "247",
  "67": "247",
  "6859": "367",
  "68": "367",
  "6961": "86",
  "69": "86",
  "7164": "298",
  "71": "298",
  "7265": "98",
  "72": "98",
  "74": "1197",
  "7369": "438",
  "73": "438",
  "7570": "136",
  "75": "136",
  "8472": "187",
  "84": "187",
  "8574": "513",
  "85": "513",
  "29": "259",
  "91": "311",
  "8777": "332",
  "87": "332",
  "8879": "146",
  "88": "146",
  "8981": "539",
  "89": "539",
  "8683": "526",
  "86": "526",
  "7785": "323",
  "77": "323",
  "7887": "381",
  "78": "381",
  "7989": "487",
  "79": "487",
  "8191": "501",
  "81": "501",
  "8293": "449",
  "82": "449",
  "3895": "16",
  "38": "16",
  "3998": "163",
  "39": "163",
  "13": "461",
  "41": "462",
  "30": "459",
  "4300": "403",
  "43": "403",
};

const cod2cit = {
  "3209": "13",
  "11": "2",
  "3412": "18",
  "3615": "21",
  "1517": "4",
  "4220": "91",
  "4522": "31",
  "4624": "34",
  "4727": "35",
  "9428": "111",
  "4930": "37",
  "19": "6",
  "5335": "46",
  "5437": "48",
  "21": "7",
  "5640": "53",
  "5743": "55",
  "5944": "57",
  "1808": "5",
  "6147": "59",
  "6249": "61",
  "6553": "62",
  "27": "10",
  "6758": "66",
  "6859": "68",
  "6961": "70",
  "7164": "72",
  "7265": "75",
  "7369": "77",
  "7570": "81",
  "8472": "94",
  "8574": "97",
  "29": "11",
  "8777": "103",
  "8879": "104",
  "8981": "109",
  "8683": "100",
  "7785": "82",
  "7887": "84",
  "7989": "86",
  "8191": "92",
  "8293": "93",
  "3895": "23",
  "3998": "24",
  "13": "1",
  "4300": "28",
};

export const cityOrDistrictToAdministrativeUnitLD = (
  cityOrDistrictId: string
): number => {
  return cod2aun[cityOrDistrictId];
};

export const cityOrDistrictToCityLD = (cityOrDistrictId: string): number => {
  return cod2cit[cityOrDistrictId];
};

export const determineSagautaVehicleClass = (
  klas: string,
  klasb: string
): number | null => {
  let s = klas;
  let carTypeId: number | null = null;
  if (s !== null && s.trim() !== "") {
    s = s.trim();
    if (s.length > 3) {
      s = s.substring(0, 3);
    }
    s = s.trim();
    if (s.trim() !== "") {
      if (
        s === "L1" ||
        s === "L2" ||
        s === "L3" ||
        s === "L4" ||
        s === "L5" ||
        s === "L6" ||
        s === "L7" ||
        s === "L"
      )
        carTypeId = 2; //MOTOCYCLES
      if (s === "M1") carTypeId = 1; //car+minbus
      if (s === "M2") carTypeId = 1; //Car+Minibus
      if (s === "M3") carTypeId = 5; //bus
      if (s === "N1") carTypeId = 3; //truck
      if (s === "N2") carTypeId = 3;
      if (s === "N3") carTypeId = 3;
      if (s === "O1") carTypeId = 8; //car trailer
      if (s === "O2") carTypeId = 9; //truck trailer
      if (s === "O3") carTypeId = 9;
      if (s === "O4") carTypeId = 9;
      if (s === "RA2") carTypeId = 7;
    }
  }
  s = klasb;
  if (s && s !== null && s.trim() !== "") {
    s = s.trim();
    if (s.length > 3) {
      s = s.substring(0, 3);
    }
    s = s.trim();
    if (s.trim() !== "") {
      if (
        s === "A1" ||
        s === "A2" ||
        s === "A3" ||
        s === "A4" ||
        s === "A5" ||
        s === "LA1" ||
        s === "LA2" ||
        s === "LA3" ||
        s === "LA4" ||
        s === "LA5"
      )
        carTypeId = 1;
      if (
        s === "C1" ||
        s === "C2" ||
        s === "C3" ||
        s === "C4" ||
        s === "C5" ||
        s === "LC1" ||
        s === "LC2" ||
        s === "LC3" ||
        s === "LC4" ||
        s === "LC5"
      )
        carTypeId = 3;
      if (s === "C6") carTypeId = 4; //self-propelled mechanism
      if (s === "E" || s === "E2" || s === "E2") carTypeId = 5; //BUS
      if (s === "B" || s === "B1" || s === "B2") carTypeId = 2; //MOTOCYCLES
      if (s === "F4" || s === "F5" || s === "F6") carTypeId = 8;
      if (s === "F7" || s === "F8" || s === "F9" || s === "F10") carTypeId = 9;
      if (s === "E3") carTypeId = 6;
      if (s === "F11-F14") carTypeId = 7;
    }
  }
  /*
  let ts = "";
  switch (carTypeId)
  {
      case 1:
          ts = "A";
          break;
      case 2:
          ts = "B";
          break;
      case 3:
          ts = "C";
          break;
      case 4:
          ts = "C6";
          break;
      case 5:
          ts = "E";
          break;
      case 6:
          ts = "E3";
          break;
      case 7:
          ts = "F11-F14";
          break;
      case 8:
          ts = "F4-F6";
          break;
      case 9:
          ts = "F7-F10";
          break;

      default:
          break;
  }
  */
  return carTypeId;
};

export const getValueForServer = (type: string, value: any): any => {
  if (type === "date") {
    if (value === null || value === undefined) {
      return null;
    }
    return formatISO(value, { representation: "date" });
  }
  //
  else if (type === "number") {
    if (value === null || value === undefined) {
      return null;
    }
    return stringToNumber(value);
  }
  //
  else if (type === "selectlist") {
    if (value === null || value === undefined) {
      return null;
    }
    return value.value;
  } else if (type === "taxonomy") {
    if (value === null || value === undefined) {
      return null;
    }
    return value.value;
  } else if (type === "taxonomyapi") {
    if (value === null || value === undefined) {
      return null;
    }
    return value;
  }
  //
  else if (type === "taxonomylabel") {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === "string") {
      return value.trim();
    }
    //
    else {
      return value;
    }
  }
  //
  else if (type === "text") {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === "string") {
      return value.trim();
    }
    //
    else {
      return value;
    }
  }
  //
  else if (type === "checkbox") {
    return value;
  }
  return null;
};

// export const handleAxiosError = (err?: any) => {
//   if (err?.isAxiosError) {
//     const response = err.response;
//     if (response) {
//       const responseData = err.response.data;
//       if (responseData) {
//         const env = process.env.REACT_APP_ENVIRONMENT;
//         const developmentEnvironments = ["L", "D", "T"];
//         const isDevOrTestEnv = developmentEnvironments.includes(env);
//         if (Array.isArray(responseData)) {
//           responseData.forEach((e: BackendError) => {
//             const translatedMessageVariables = e.messageVariables.map(
//               (item: string) => {
//                 return i18next.t(item.replaceAll(":", " "));
//               }
//             );
//             let message = e.messageCode;
//             if (isDevOrTestEnv) {
//               message = e?.exceptionMessage || e?.formatedMessage;
//               toast.error(i18next.t(message));
//             } else {
//               toast.error(i18next.t(message, translatedMessageVariables));
//             }
//           });
//         }
//         //
//         else {
//           let message = responseData.messageCode;
//           if (message) {
//             if (isDevOrTestEnv) {
//               message =
//                 responseData?.exceptionMessage || responseData?.formatedMessage;
//             }

//             const translatedMessageVariables =
//               responseData.messageVariables.map((item: string) => {
//                 return i18next.t(item.replaceAll(":", " "));
//               });
//             toast.error(i18next.t(message, translatedMessageVariables));
//           } else {
//             if (responseData.message) {
//               toast.error(responseData.message);
//             } else {
//               toast.error(responseData);
//             }
//           }
//         }
//       } else {
//         toast.error(err?.toString());
//       }
//     } else {
//       toast.error(err?.toString());
//     }
//   } else {
//     toast.error(err?.toString());
//   }
// };

export const transformForForm = ({
  obj,
  config,
  t,
  taxonomy,
}: {
  obj: { [key: string]: any };
  config: any;
  t: TFunction;
  taxonomy: TaxonomyState;
}) => {
  return Object.entries(obj).reduce((result, [key, value]) => {
    const fieldConfig = config[key];
    if (!fieldConfig) {
      result[key] = value;
      return result;
    }
    if (fieldConfig.type === "date") {
      if (value === null || value === undefined) {
        result[key] = null;
        return result;
      }
      result[key] = new Date(value.substring(0, 10));
    } else if (fieldConfig.type === "number") {
      if (value === null || value === undefined) {
        result[key] = "";
        return result;
      }
      if (fieldConfig.format === "integer") {
        result[key] = value.toString();
      } else {
        result[key] = value.toLocaleString("de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
    } else if (fieldConfig.type === "taxonomy") {
      if (value === null || value === undefined) {
        result[key] = null;
        return result;
      }

      const taxonomyObject = taxonomy[fieldConfig.taxonomyKey]?.byId[value];
      if (!taxonomyObject) {
        result[key] = null;
        return result;
      }
      result[key] = {
        label: t(taxonomyObject.code),
        value,
      };
    } else if (fieldConfig.type === "selectlist") {
      if (value === null || value === undefined) {
        result[key] = null;
        return result;
      }
      result[key] = {
        label: value,
        value,
      };
    } else if (fieldConfig.type === "text") {
      result[key] = value === null || value === undefined ? "" : value;
    } else if (fieldConfig.type === "searchtext") {
      result[key] = value === null || value === undefined ? "" : value;
    } else if (fieldConfig.type === "checkbox") {
      result[key] = value;
    }
    return result;
  }, {});
};
